import { PlainDate } from "@sa-nu/shared-utils/date";
import Image from "next/image";

import type { AccommodationReservationProvider } from "../../../../__generated__/gql/graphql";
import { useRuleRevision2027AsOfToday } from "../../../_hooks/transition/use-rule-revision-2027";

const FIRST_NIGHT_RESERVABLE_BY_NEW_LEAD_TIME = PlainDate.from("2027-04-01");

const RESERVABLE_MESSAGE = "2027/4/1以降の予約が可能になりました";
const MORE_ROOMS_MESSAGE = "2027/4/1以降、予約可能な部屋が増えました";

type Notice = { message: string; helpUrl: string | null };

// Staff などヘルプページの案内対象外のプランは、ヘルプリンク無しの既定表示にする
const DEFAULT_NOTICE: Notice = {
  message: RESERVABLE_MESSAGE,
  helpUrl: null,
};

const NOTICE_BY_PROVIDER: Partial<
  Record<AccommodationReservationProvider["__typename"], Notice>
> = {
  StandardPlanAccommodationReservationProvider: {
    message: RESERVABLE_MESSAGE,
    helpUrl: "https://www.sa-nu.com/magazine/news-release/weekday-new-rule",
  },
  CoOwnersPlanAccommodationReservationProvider: {
    message: MORE_ROOMS_MESSAGE,
    helpUrl: "https://help.sa-nu.com/hc/ja/articles/26994609673497",
  },
  FullOwnerPlanAccommodationReservationProvider: {
    message: MORE_ROOMS_MESSAGE,
    helpUrl: "https://sanu.notion.site/Owners-3e9d14f248a8805593d3c8fad19b061f",
  },
  BusinessPlanAccommodationReservationProvider: {
    message: RESERVABLE_MESSAGE,
    helpUrl: "https://help.sa-nu.com/hc/ja/articles/61977807468953",
  },
};

export function ReservablePeriodExtendedNotice({
  provider,
  maxNight,
  onJump,
}: {
  provider: AccommodationReservationProvider;
  maxNight: PlainDate;
  onJump: (date: PlainDate) => void;
}) {
  const { status } = useRuleRevision2027AsOfToday({ provider });

  // 表示範囲が 4/1 に届かない画面（チケットの有効期間など）では、ジャンプできず文言も事実と合わないので表示しない
  if (PlainDate.compare(maxNight, FIRST_NIGHT_RESERVABLE_BY_NEW_LEAD_TIME) < 0)
    return null;

  // 案内開始日〜適用開始宿泊日の前日だけ改定のヘルプへ誘導する。法人はこの期間が法人ごとに異なる
  const notice = NOTICE_BY_PROVIDER[provider.__typename] ?? DEFAULT_NOTICE;
  const helpUrl = status === "AnnouncementStarted" ? notice.helpUrl : null;

  return (
    <div className="bg-slightbeige w-full px-[28px] pb-[12px] md:px-0">
      <div className="border-sbeige bg-swhite flex items-center justify-between gap-[8px] rounded-[4px] border p-[12px]">
        <div className="flex flex-col gap-[8px]">
          <p className="text-skogecha text-[10px] leading-[1.2] font-bold tracking-[0.4px] md:text-[13px]">
            {notice.message}
          </p>
          {helpUrl && (
            <a
              data-analytics-id="reservation_calendar_clicked_rule_revision_help"
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-[2px] hover:opacity-70"
            >
              <Image
                src="/i/icon_question.svg"
                alt="Information"
                width={16}
                height={16}
              />
              <span className="border-b-sorange text-sorange border-b border-dotted pb-px text-[11px] leading-none font-medium tracking-[0.22px] md:text-[13px]">
                予約ルールの改定について
              </span>
            </a>
          )}
        </div>
        <button
          data-analytics-id="reservation_calendar_clicked_rule_revision_jump"
          type="button"
          onClick={() => onJump(FIRST_NIGHT_RESERVABLE_BY_NEW_LEAD_TIME)}
          className="border-skogecha bg-swhite text-skogecha flex h-[30px] shrink-0 items-center rounded-full border px-[8px] text-[11px] font-bold tracking-[0.44px] hover:opacity-70"
        >
          4/1以降へ
        </button>
      </div>
    </div>
  );
}
